// creature-summary.ts — client-side render of the creature SUMMARY ROW
// (essences/defenses, V·Mana·AC·Luck·AP bars, speeds/senses, combat extras),
// reading from window.FateTide.casterCache (no DB ping on a cache hit).
//
// Replaces the PHP renderers in includes/functions/janus/core/
// creature_summary_renderers.php (render_creature_summary_row +
// render_essence_defense_section + render_speed_senses_section +
// render_combat_extras_section + ft_render_summary_combat_extras_cells_sideinfo +
// ft_summary_caster_has_visible_senses) AND the bars from
// includes/functions/encounter_creatures/build_v_mana_ecac_luck_bars_inc.php
// (note: that PHP function STAYS — it has 3 other callers; only the row's copy of
// its logic is ported here).
//
// Renders INLINE into the creature card on load (renderCreatureSummaryInline),
// fetch-on-miss via fetchAndCacheCaster. Mirrors spells-prepared-info.ts. All
// onclick handlers (useAbility / openItemInfo / forceOpenSideInfoPanel /
// render*InfoPanel) dispatch through the delegated summary listener —
// every target already lives on window. Data sources match the PHP 1:1:
//   essences  → computed.creature.essences.<var>.total   (compute_essence)
//   skills    → computed.creature.skills.<skill_var>      (tier_level / adv_dis_stacks)
//   vitality  → computed.vitality.*  (ftComputedValue bridge; v_arr fallback)
//   speeds    → computed.creature.speeds.<short>          (ftLayerValue bridge)
//   senses    → computed.creature.senses.<var>            (ftLayerValue bridge)
//   attacks   → computed.creature.attacks.per_turn.total
//   ec/ac     → ec_total / ac_total                       (ftLayerValue bridge)
//   reach / damage_mitigation / light / blindsight        → ftLayerValue (layers)
//   mana / luck / aps / reaction                          → encounter_creatures

import { hasPlayerPermission } from '../shared/array-types';
import { paintMarkup } from '../player-character-sheet/paint-markup';
import type { CachedCaster, AcPart, SkillEntry } from './types';

// ── Static catalogs (additions.php). Order = render order (matches *::getAll()). ──
const ESSENCE_CORE: ReadonlyArray<{ item_id: string; item_name: string; variable_name: string }> = [
    { item_id: '22918408-67CC-4093-80E8-BF6FA8915C21', item_name: 'Body', variable_name: 'body' },
    { item_id: '63ABC96E-3804-4E1F-B38D-AABA3237B2D8', item_name: 'Mind', variable_name: 'mind' },
    { item_id: '72F93730-134F-4CA2-8FC4-F2915F20CAC6', item_name: 'Soul', variable_name: 'soul' },
];

// Essence::getDefenses — DefensiveSkill rows grouped by essence variable_name.
const ESSENCE_DEFENSES: Record<string, ReadonlyArray<{ ability_id: string; skill_var: string }>> = {
    body: [
        { ability_id: '59C08B6D-F8D4-F9DB-81D9-0FD592094C73', skill_var: 'balance' },
        { ability_id: '24C07918-39D5-4317-A958-BDF68B633A21', skill_var: 'fortitude' },
    ],
    mind: [{ ability_id: '58A62523-2588-452B-876B-61D8ACF3CCED', skill_var: 'psyche' }],
    soul: [{ ability_id: '922C5882-54DE-42D0-A5C8-8118AA1C858E', skill_var: 'willpower' }],
};

const SPEED_TYPES: ReadonlyArray<{ item_id: string; variable_name: string }> = [
    { item_id: 'DF516479-F164-87FA-7BB8-6F268BF1DCE7', variable_name: 'speed_walking' },
    { item_id: 'B96061AE-3369-7E7A-B6A2-C7C8FBF2A23F', variable_name: 'speed_jumping' },
    { item_id: 'ED9810A9-3456-4D6B-BC5C-A1BAF5952B98', variable_name: 'speed_swimming' },
    { item_id: 'EDCBE2A8-CD3D-43DC-9B92-84A634BE12AE', variable_name: 'speed_climbing' },
    { item_id: 'F1D0A27A-43F1-4A4C-A5F8-ED9BD2D908D4', variable_name: 'speed_flying' },
    { item_id: 'ACFB413D-15A8-835B-C821-9E101B6C4F60', variable_name: 'speed_burrowing' },
    { item_id: '3C96DE74-D9E6-4173-8671-989209A0BF97', variable_name: 'teleport' },
    { item_id: 'AE7D4EB3-DC36-BD47-978F-1DBCFE2B616A', variable_name: 'speed_hovering' },
];

const SENSES: ReadonlyArray<{ item_id: string; variable_name: string }> = [
    { item_id: '3B58A8C2-F310-42FB-A71C-904904089303', variable_name: 'nightsight' },
    { item_id: 'F87E66D1-0EB2-469F-BB07-CF2CF4E26B30', variable_name: 'blindsight' },
    { item_id: '51EFBF8C-672F-F1F8-543A-6B079320572C', variable_name: 'fiendsight' },
    { item_id: 'E472D6BD-1A9C-F84E-AD55-F3068A3EC303', variable_name: 'ghostsight' },
    { item_id: '6B8E5648-B589-5F6A-DFAA-84F7E646B062', variable_name: 'spellsight' },
    { item_id: '9F34BE02-A199-4A9C-A2C4-531CD8A1DD58', variable_name: 'truesight' },
    { item_id: '61FD588C-4A17-4190-999B-7832820C44BA', variable_name: 'tremorsense' },
    { item_id: '57DAF94A-7C3A-4C5B-A7A9-22729D06355F', variable_name: 'telepathy' },
];

const REACH_ICON_UUID = '36D59FF4-6778-96ED-8664-F5D63638C77B'; // RangeType::getId('reach')
const BLINDSIGHT_BLIND_UUID = '7D85731F-3AAF-7E09-673E-AE6A0BFB3490';
const CDN_ICONS = 'https://cdn.fatetide.com/images/icons/';

type CreatureSummaryWindow = Record<string, unknown>;

function csCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as CreatureSummaryWindow)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

function csAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-cs-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        // HTML lowercases attribute names, so `data-ft-casterId` reads back as dataset.ftCasterid,
        // NOT dataset.ftCasterId. Kebab-case the key so the camelCase dataset readers resolve.
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function csOpenPanelAttrs(panel: string, casterId: string, extra: Record<string, string> = {}): string {
    return csAttrs('open-panel', { panel, casterId, ...extra });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-cs-action]')
        : null;
    if (!el) return;
    const action = el.dataset.ftCsAction || '';
    if (action === 'use-ability') {
        event.preventDefault();
        csCall('useAbility', el.dataset.ftAbilityId || '');
        return;
    }
    if (action === 'open-panel') {
        event.preventDefault();
        csCall('forceOpenSideInfoPanel');
        const casterId = el.dataset.ftCasterId || '';
        const panel = el.dataset.ftPanel || '';
        if (panel === 'essence') csCall('renderEssenceInfoPanel', casterId, el.dataset.ftVariable || '');
        else if (panel === 'health') csCall('renderCreatureHealthInfoPanel', casterId);
        else if (panel === 'mana-spirit') csCall('renderManaSpiritInfoPanel', casterId);
        else if (panel === 'armor-class') csCall('renderArmorClassInfoPanel', casterId);
        else if (panel === 'damage-mitigation') csCall('renderDamageMitigationInfoPanel', casterId);
        else if (panel === 'luck-fortune') csCall('renderLuckFortuneInfoPanel', casterId);
        else if (panel === 'attacks') csCall('renderAttacksInfoPanel', casterId);
        else if (panel === 'reach') csCall('renderReachInfoPanel', casterId);
        else if (panel === 'movement-sense') csCall('renderMovementSenseInfoPanel', casterId, el.dataset.ftVariable || '');
        return;
    }
});

// ── Speed/sense badge gradients (shared visual language across the creature card,
// the encounter-creature summary row, and the PC sheet summary bar — keep these in
// sync with tabs/summary.ts). SPEED = cool blue-grey, SENSE = muted green, and the
// PREFERRED speed type gets a gold/amber "highlighter" gradient + amber glow ring so
// the preferred_speed_type stands out distinctly from the speeds AND the green senses. ──
const SPEED_BADGE_STYLE = 'background:linear-gradient(135deg,#243043,#3c5573);color:#fff;';
const SENSE_BADGE_STYLE = 'background:linear-gradient(135deg,#1f3a2e,#356b4f);color:#fff;';
const PREFERRED_SPEED_BADGE_STYLE = 'background:linear-gradient(135deg,#f2a900,#ffd633);color:#2b2200;text-shadow:0 1px 1px rgba(255,255,255,.4);box-shadow:0 0 0 2px rgba(255,196,0,.6);border-color:#ffe893;';

// ── Tiny readers (twins of the PHP helpers). ──────────────────────────────────
function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}
function num(v: unknown): number {
    const n = Number(unwrap(v));
    return Number.isFinite(n) ? n : 0;
}
/** PHP display_value_or_dash twin: '—' for 0/empty, else the value (incl. negatives). */
function dashIfZero(v: unknown): string {
    const n = num(v);
    return n === 0 ? '—' : String(n);
}
/** Normalize a speed-type pointer to its UUID. preferred_speed_type can arrive as a
 *  nested {items:{item_id}} object (the reshaped blob), a {item_id} shell, a [uuid]
 *  array, or a bare uuid string — pull the item_id out of whichever shape it is. */
function speedTypeId(v: unknown): string {
    if (v == null) return '';
    const u = unwrap(v);
    if (u && typeof u === 'object') {
        const o = u as Record<string, unknown>;
        const items = o.items as Record<string, unknown> | undefined;
        const id = (items && typeof items === 'object') ? items.item_id : o.item_id;
        return String(unwrap(id) ?? '');
    }
    return String(u ?? '');
}
function ecArrOf(caster: CachedCaster): Record<string, unknown> {
    const ec = caster.encounter_creatures;
    return (ec && !Array.isArray(ec)) ? (ec as Record<string, unknown>) : {};
}
function actionPointCounts(ecArr: Record<string, unknown>): { remaining: number; spent: number } {
    const remaining = Math.max(0, Math.trunc(num(ecArr.aps)));
    const totalRaw = unwrap(ecArr.aps_total);
    const totalNumber = Number(totalRaw);
    // aps is the authoritative remaining count and aps_total is the persistent maximum. aps_used is
    // only a derived lean-blob companion and can be absent on a surgical/intermediate snapshot, so
    // prefer total - remaining whenever total is actually present. Temporary AP may exceed the
    // persistent maximum (Haste); in that case every current point is available and spent stays zero.
    const spent = totalRaw !== null && totalRaw !== undefined && totalRaw !== '' && Number.isFinite(totalNumber)
        ? Math.max(0, Math.trunc(totalNumber) - remaining)
        : Math.max(0, Math.trunc(num(ecArr.aps_used)));
    return { remaining, spent };
}
/**
 * Mana bar width. `mana_percent` is a DERIVED companion the server recomputes as
 * floor(mana / spirit * 100) from the encounter_creatures scalars (update_encounter_creature_stats §3,
 * build_v_arr set_authority_scalar) and lean-elides when 0. The two operands travel in the same section,
 * so derive from them with the SAME arithmetic whenever both are present — a stale companion can then
 * never disagree with the authoritative mana it sits beside. The stored percent is only the fallback
 * for a row without a usable spirit divisor. (Codex checkpoint 2, 2026-08-28.)
 */
function manaPercentOf(ecArr: Record<string, unknown>): number {
    const spiritRaw = unwrap(ecArr.spirit);
    const spirit = Number(spiritRaw);
    if (spiritRaw !== null && spiritRaw !== undefined && spiritRaw !== '' && Number.isFinite(spirit) && spirit > 0) {
        return Math.floor(Math.max(0, num(ecArr.mana)) / spirit * 100);
    }
    return num(ecArr.mana_percent);
}
function casterIdOf(caster: CachedCaster, fallbackId: string): string {
    const ecId = String(unwrap(ecArrOf(caster).encounter_creature_id) ?? '').trim();
    return ecId !== '' ? ecId : fallbackId;
}
function itemTypeOf(caster: CachedCaster): string {
    // PC-in-encounter blobs carry no top-level `items` bucket — item_type lives on
    // encounter_creatures.item_type. Fall back there so the player_character gate (mana bar,
    // canOpenPanels, lair summary) resolves correctly. Treat an EMPTY items.item_type as absent so a
    // present-but-blank items value can't suppress a valid ec value (`??` only catches null/undefined).
    const _itemsIt = unwrap(caster.items?.item_type);
    return String(
        (_itemsIt !== undefined && _itemsIt !== null && _itemsIt !== '')
            ? _itemsIt
            : (unwrap(ecArrOf(caster).item_type) ?? ''),
    );
}
/** item_type !== 'player_character' || has_player_permission() — the clickability gate. */
function canOpenPanels(caster: CachedCaster): boolean {
    return itemTypeOf(caster) !== 'player_character' || hasPlayerPermission(caster);
}

// computed cluster part → numeric total (bare number or {total} provenance wrapper).
function partTotal(part: AcPart | number | boolean | undefined): number {
    if (part === undefined || part === null || typeof part === 'boolean') return 0;
    return (typeof part === 'object') ? num(part.total) : num(part);
}

// PHP layer_value() bridges speed_*/sense_*/ec_total/ac_total/damage_mitigation to the
// computed.creature.* clusters; ftLayerValue() does NOT (it only reads the raw
// item_layer_types bucket + carriers). So read those values from the computed clusters
// directly — derived speeds (½-walk swim/climb, essence jump) and status-derived
// senses/mitigation have no raw layer and would otherwise read 0.
function speedShort(varName: string): string {
    return varName === 'teleport' ? 'teleport' : varName.slice('speed_'.length);
}
function speedTotal(caster: CachedCaster, varName: string): number {
    const speeds = caster.computed?.creature?.speeds as Record<string, AcPart | number | boolean> | undefined;
    return partTotal(speeds?.[speedShort(varName)]);
}
function senseTotal(caster: CachedCaster, varName: string): number {
    const senses = caster.computed?.creature?.senses as Record<string, AcPart | number> | undefined;
    return partTotal(senses?.[varName]);
}
// Spirit (mana cap) — PHP layer_value('spirit') bridges to the computed essence; ftLayerValue does
// NOT, so read computed.creature.essences.spirit.total first (then ec.spirit, then the raw layer).
function spiritMax(caster: CachedCaster): number {
    const e = caster.computed?.creature?.essences?.spirit;
    if (e !== undefined && e !== null) return partTotal(e as unknown as number);
    const ecS = ecArrOf(caster).spirit;
    if (ecS !== undefined && ecS !== null) return num(ecS);
    return num(ftLayerValue(caster, 'spirit'));
}
function acClusterTotal(caster: CachedCaster, key: 'ec' | 'ac' | 'mitigation', fallbackLayer: string): number {
    const cluster = (caster.computed?.creature?.ac as Record<string, AcPart | number> | undefined)?.[key];
    if (cluster !== undefined && cluster !== null) return partTotal(cluster);
    return num(ftLayerValue(caster, fallbackLayer));
}

// ── ESSENCE / DEFENSE ─────────────────────────────────────────────────────────
function renderEssenceDefense(caster: CachedCaster, casterId: string, sideInfo: boolean): string {
    const skills = (caster.computed?.creature?.skills ?? caster.skills ?? {}) as Record<string, SkillEntry>;
    const essences = (caster.computed?.creature?.essences ?? caster.computed?.essences ?? {}) as Record<string, { total?: number }>;

    const outerCls = sideInfo ? 'd-flex col-12 mb-2' : 'd-flex col-lg-3 col-md-12';
    let html = `<div class="${outerCls} justify-content-center align-items-start">`;

    for (const ess of ESSENCE_CORE) {
        const pool = num(essences[ess.variable_name]?.total ?? 0);
        const defenses = ESSENCE_DEFENSES[ess.variable_name] ?? [];

        html += '<div class="col text-center">';
        // Essence icon
        html += `<div class="mb-2"><span role="button" class="text-primary" ${csOpenPanelAttrs('essence', casterId, { variable: ess.variable_name })}>${iconMap(ess.item_id, 25, 25, `View ${ess.item_name} details`)}</span></div>`;
        // Essence pool button
        html += `<div class="mb-2"><button type="button" class="btn btn-sm btn-light" ${csAttrs('use-ability', { abilityId: ess.item_id })} data-bs-toggle="tooltip" title="${escapeHtml(ess.item_name)}">${dashIfZero(pool)}</button></div>`;

        // Defensive abilities (vertical stack)
        if (defenses.length > 0) {
            html += '<div class="d-flex flex-column gap-1 align-items-center">';
            for (const def of defenses) {
                const skill = skills[def.skill_var];
                const tier = num((skill as Record<string, unknown> | undefined)?.tier_level);
                const advDis = num(skill?.adv_dis_stacks);
                const advTitle = advDis !== 0 ? ` (${advDis > 0 ? '+' : ''}${advDis} adv/dis)` : '';
                const skillTitle = def.skill_var.charAt(0).toUpperCase() + def.skill_var.slice(1);

                html += `<button type="button" class="btn btn-light position-relative d-inline-flex align-items-center justify-content-center" style="width: 36px; height: 36px; padding: 4px; flex-shrink: 0;" ${csAttrs('use-ability', { abilityId: def.ability_id })} data-bs-toggle="tooltip" title="${escapeHtml(skillTitle + advTitle)}">`;
                html += iconMap(def.ability_id, 24, 24, false);
                if (tier > 0) {
                    html += `<div class="position-absolute d-flex align-items-center justify-content-center bg-info text-white fw-bold border border-2 border-dark" style="top: 0; right: 0; width: 18px; height: 18px; border-radius: 50%; font-size: 0.75rem; line-height: 1;">${tier}</div>`;
                }
                if (advDis !== 0) {
                    html += `<span class="position-absolute text-${advDis > 0 ? 'success' : 'danger'} fw-bold" style="bottom: 1px; right: 2px; font-size: 0.7rem;">${advDis > 0 ? '▲' : '▼'}${Math.abs(advDis)}</span>`;
                }
                html += '</button>';
            }
            html += '</div>';
        }
        html += '</div>';
    }
    html += '</div>';
    return html;
}

// ── V · MANA · AC · LUCK · AP BARS (build_v_mana_ecac_luck_bars, in_narrow_summary=false) ──
function vit(caster: CachedCaster, key: string): unknown {
    const v = ftComputedValue(caster, 'vitality', key);
    if (v != null && v !== '') return v;
    const va = caster.v_arr?.[key];
    return Array.isArray(va) ? va[0] : va;
}
function acNum(caster: CachedCaster, which: 'ec' | 'ac'): number {
    return acClusterTotal(caster, which, which + '_total');
}

function renderVManaEcacLuckBars(caster: CachedCaster, casterId: string, narrow = false): string {
    const ecArr = ecArrOf(caster);
    const ecId = String(unwrap(ecArr.encounter_creature_id) ?? '');
    const itemType = itemTypeOf(caster);
    const spirit = spiritMax(caster);
    const hasSpirit = spirit > 0;
    const darkMode = /(?:^|;\s*)dark_mode=1(?:;|$)/.test(document.cookie);
    const canOpen = canOpenPanels(caster);

    const vCurrent = num(vit(caster, 'v_current'));
    const vMax = num(vit(caster, 'v_max'));
    const effRaw = vit(caster, 'effective_v_max');
    const effMax = (effRaw != null && effRaw !== '') ? num(effRaw) : vMax;
    const vMaxTemp = num(vit(caster, 'v_max_temp'));
    const vMaxReduction = num(vit(caster, 'v_max_reduction'));
    const vTemp = num(vit(caster, 'v_temp'));
    const vPct = num(vit(caster, 'v_bar_v_percent'));
    const vTempPct = num(vit(caster, 'v_bar_v_temp_percent'));

    let html = `<div class="col-12 w-100${narrow ? ' px-2' : ''}">`;

    // Vitality bar
    html += `<div class="d-flex align-items-center justify-content-center mb-1" role="button" ${csOpenPanelAttrs('health', casterId)}>`;
    html += `<div class="mr-1"><div>${iconMap('vitality')}</div></div>`;
    html += '<div class="flex-grow-1">';
    html += `<div class="progress border position-relative" role="progressbar" aria-label="v_bar" aria-valuenow="${vPct + vTempPct}" aria-valuemin="0" aria-valuemax="100">`;
    let vLabel = `${vCurrent} / ${effMax}`;
    if (vMaxTemp) vLabel += `(+${vMaxTemp})`;
    if (vMaxReduction) vLabel += ` (-${vMaxReduction})`;
    html += `<div class="progress-bar bg-danger text-dark overflow-visible" style="width: ${vPct}%">${vLabel}</div>`;
    if (vTempPct > 0) {
        html += `<div class="progress-bar bg-warning text-dark overflow-visible" style="width: ${vTempPct}%">+${vTemp}</div>`;
    }
    html += '</div></div></div>';

    // Mana bar (conditional)
    const showMana = hasSpirit && (
        itemType === 'player_character' ||
        (itemType === 'creature' && hasPlayerPermission(caster)) ||
        ecId === ''
    );
    if (showMana) {
        const isItemCreature = ecId === '';
        const manaDisplay = isItemCreature ? spirit : num(ecArr.mana);
        const manaPct = isItemCreature ? 100 : manaPercentOf(ecArr);
        const open = canOpen
            ? ` role="button" ${csOpenPanelAttrs('mana-spirit', casterId)}`
            : ' style="pointer-events: none; cursor: default;" aria-disabled="true"';
        html += `<div class="d-flex align-items-center justify-content-center mb-1${canOpen ? '' : ' opacity-90'}"${open}>`;
        html += `<div class="mr-1"><div>${iconMap('mana')}</div></div>`;
        html += '<div class="flex-grow-1"><div class="col-12 mb-1">';
        html += `<div class="progress border" role="progressbar" aria-label="mana_bar" aria-valuenow="${manaPct}" aria-valuemin="0" aria-valuemax="100">`;
        html += `<div class="progress-bar bg-info text-dark overflow-visible" style="width: ${manaPct}%">${manaDisplay} / ${spirit}</div>`;
        html += '</div></div></div></div>';
    }

    // AC + Luck/Fortune row
    html += '<div class="d-flex justify-content-center mb-1">';
    html += `<div class="d-flex justify-content-center align-items-center border rounded mr-1" role="button" ${csOpenPanelAttrs('armor-class', casterId)}>`;
    html += `<div class="p-1 border-end">${iconMap('armor')}</div>`;
    html += `<div class="p-1 border-end border-primary">${acNum(caster, 'ec')}</div>`;
    html += `<div class="p-1">${acNum(caster, 'ac')}</div>`;
    html += '</div>';

    const luckOpen = canOpen
        ? ` role="button" ${csOpenPanelAttrs('luck-fortune', casterId)}`
        : ' style="pointer-events: none; cursor: default;" aria-disabled="true"';
    html += `<div id="div_luck_fortune" class="d-flex justify-content-center align-items-center border rounded p-1${canOpen ? '' : ' opacity-90'}"${luckOpen}>`;
    html += `<div class="position-relative">${iconMap('luck')}`;
    const luckRaw = ecArr.luck;
    const luck = (luckRaw === null || luckRaw === undefined) ? null : num(luckRaw);
    if (luck !== null && luck > 0) {
        html += `<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-info border">${luck}</span>`;
    } else if (luck !== null && luck < 0) {
        html += `<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border">${luck}</span>`;
    }
    html += '</div></div></div>';

    // Action Points
    if (ecArr.encounter_id) {
        html += '<div class="d-flex flex-wrap align-items-center justify-content-center ft-pc-ap-row">';
        const ap = actionPointCounts(ecArr);
        for (let x = 0; x < ap.remaining; x++) {
            const src = darkMode ? 'ap_unused_dark_mode.png' : 'ap_unused.png';
            html += `<div class="px-1"><img src="${CDN_ICONS}${src}" width="15" height="15"></div>`;
        }
        for (let x = 0; x < ap.spent; x++) {
            const src = darkMode ? 'ap_used_dark_mode.png' : 'ap_used.png';
            html += `<div class="px-1"><img src="${CDN_ICONS}${src}" width="15" height="15"></div>`;
        }
        // Reaction is a combat resource: 1 = available, anything else = used. A spent reaction is
        // stored as 0, which the lean EC-blob build (build_encounter_creature_arr.php) ELIDES →
        // arrives here as undefined. In combat every EC always has a reaction slot, so never hide
        // the pip — show the crossed-out "used" art for absent/0 instead of nothing. (Mirrors
        // creature-thumbnail.ts; the old `=== 0` branch was unreachable after elision.)
        const reactionAvailable = num(ecArr.reaction) === 1;
        const reactionSrc = reactionAvailable
            ? (darkMode ? 'ap_reaction_dark_mode.png' : 'ap_reaction.png')
            : (darkMode ? 'ap_reaction_used_dark_mode.png' : 'ap_reaction_used.png');
        html += `<div class="px-1"><img src="${CDN_ICONS}${reactionSrc}" width="15" height="15"></div>`;
        html += '</div>';
    }

    html += '</div>';
    return html;
}

// ── COMBAT EXTRAS (shared cell builders) ──────────────────────────────────────
function totalAttacks(caster: CachedCaster): number {
    const perTurn = (caster.computed?.creature?.attacks as Record<string, AcPart | number> | undefined)?.per_turn;
    const t = partTotal(perTurn);
    return t > 0 ? t : 1; // every creature gets at least 1 attack
}

/** Side-info combat cells (ft_render_summary_combat_extras_cells_sideinfo). No outer wrapper. */
function renderCombatExtrasCellsSideinfo(caster: CachedCaster, casterId: string, leadingDivider: boolean): string {
    const cell = 'd-flex flex-column justify-content-center text-center col-3.5 p-1 flex-shrink-0';
    const canOpen = canOpenPanels(caster);
    const reach = ftLayerValue(caster, 'reach');
    const mit = acClusterTotal(caster, 'mitigation', 'damage_mitigation');
    const lead = leadingDivider ? ' border-start border-secondary ps-2 ms-1' : '';

    // Attacks (first cell gets the leading divider)
    let html = `<div class="${cell}${lead}">`;
    if (canOpen) {
        html += `<span role="button" class="d-inline-flex flex-column align-items-center w-100" ${csOpenPanelAttrs('attacks', casterId)}><div>${iconMap('unhindered_attacks')}</div><div><span class="badge bg-dark border">${totalAttacks(caster)}</span></div></span>`;
    } else {
        html += `<div>${iconMap('unhindered_attacks')}</div><div><span class="badge bg-dark border">${totalAttacks(caster)}</span></div>`;
    }
    html += '</div>';

    html += `<div class="${cell}">`;
    if (canOpen) {
        html += `<span role="button" class="d-inline-flex flex-column align-items-center w-100" data-ft-info-item-id="${escapeHtml(REACH_ICON_UUID)}" ${csOpenPanelAttrs('reach', casterId)}><div>${iconMap('reach')}</div><div><span class="badge bg-dark border">${num(reach)}</span></div></span>`;
    } else {
        html += `<div>${iconMap('reach')}</div><div><span class="badge bg-dark border">${num(reach)}</span></div>`;
    }
    html += '</div>';

    if (mit > 0) {
        html += `<div class="${cell}">`;
        if (canOpen) {
            html += `<span role="button" class="d-inline-flex flex-column align-items-center w-100" ${csOpenPanelAttrs('damage-mitigation', casterId)}><div>${iconMap('damage_mitigation')}</div><div><span class="badge bg-dark border">${mit}</span></div></span>`;
        } else {
            html += `<div>${iconMap('damage_mitigation')}</div><div><span class="badge bg-dark border">${mit}</span></div>`;
        }
        html += '</div>';
    }

    const light = ftLayerValue(caster, 'bright_light_dim_light');
    if (light != null && light !== '' && num(light) !== 0) {
        html += `<div class="${cell}" style="min-width: 3.5rem;"><div class="d-flex flex-wrap justify-content-center align-items-center gap-1">${iconMap('bright_light_dim_light')}<span class="badge bg-dark border">${num(light)}</span>`;
        if (num(ftLayerValue(caster, 'magical_light')) !== 0) html += iconMap('magical_light');
        if (num(ftLayerValue(caster, 'magical_darkness')) !== 0) html += iconMap('magical_darkness');
        html += '</div></div>';
    }
    return html;
}

/** Standalone combat-extras column (render_combat_extras_section). Non-side-info only. */
function renderCombatExtras(caster: CachedCaster, casterId: string): string {
    const canOpen = canOpenPanels(caster);
    const reach = ftLayerValue(caster, 'reach');
    const mit = acClusterTotal(caster, 'mitigation', 'damage_mitigation');

    let html = '<div class="col text-center border-start">';

    // Total attacks
    html += '<div class="align-middle p-1 border-bottom" style="min-height:65px;">';
    if (canOpen) {
        html += `<span role="button" class="d-inline-flex flex-column align-items-center" ${csOpenPanelAttrs('attacks', casterId)}><div>${iconMap('unhindered_attacks')}</div><div><span class="badge bg-dark border">${totalAttacks(caster)}</span></div></span>`;
    } else {
        html += `<div>${iconMap('unhindered_attacks')}</div><div><span class="badge bg-dark border">${totalAttacks(caster)}</span></div>`;
    }
    html += '</div>';

    // Reach
    html += '<div class="align-middle p-1" style="min-height:65px;">';
    if (canOpen) {
        html += `<span role="button" class="d-inline-flex flex-column align-items-center" data-ft-info-item-id="${escapeHtml(REACH_ICON_UUID)}" ${csOpenPanelAttrs('reach', casterId)}><div>${iconMap('reach')}</div><div><span class="badge bg-dark border">${num(reach)}</span></div></span>`;
    } else {
        html += `<div>${iconMap('reach')}</div><div><span class="badge bg-dark border">${num(reach)}</span></div>`;
    }
    html += '</div>';

    // Damage mitigation
    if (mit > 0) {
        html += '<div class="align-middle p-1" style="min-height:65px;">';
        if (canOpen) {
            html += `<span role="button" class="d-inline-flex flex-column align-items-center" ${csOpenPanelAttrs('damage-mitigation', casterId)}><div>${iconMap('damage_mitigation')}</div><div><span class="badge bg-dark border">${mit}</span></div></span>`;
        } else {
            html += `<div>${iconMap('damage_mitigation')}</div><div><span class="badge bg-dark border">${mit}</span></div>`;
        }
        html += '</div>';
    }

    // Light aura
    const light = ftLayerValue(caster, 'bright_light_dim_light');
    if (light != null && light !== '' && num(light) !== 0) {
        html += `<div class="align-middle p-1"><div>${iconMap('bright_light_dim_light')}<span class="badge bg-dark border">${num(light)}</span>`;
        if (num(ftLayerValue(caster, 'magical_light')) !== 0) html += iconMap('magical_light');
        if (num(ftLayerValue(caster, 'magical_darkness')) !== 0) html += iconMap('magical_darkness');
        html += '</div></div>';
    }

    html += '</div>';
    return html;
}

// ── SPEEDS / SENSES ───────────────────────────────────────────────────────────
function hasVisibleSenses(caster: CachedCaster): boolean {
    for (const sense of SENSES) {
        if (senseTotal(caster, sense.variable_name) !== 0) return true;
    }
    return num(ftLayerValue(caster, 'blindsight_blind')) !== 0;
}

/** speed_integer_layers_are_absent_for_default_derivation twin (raw layer presence). */
function speedHasExplicitLayer(caster: CachedCaster, layerName: string): boolean {
    const ilt = caster.item_layer_types;
    const slot = ilt?.[layerName];
    if (slot == null) return false;
    const entries = Array.isArray(slot) ? slot : [slot];
    return entries.some((e) => {
        if (e == null) return false;
        if (typeof e === 'object') return Object.keys(e).length > 0;
        return String(e).trim() !== '';
    });
}

function renderSpeedSenses(caster: CachedCaster, casterId: string, sideInfo: boolean): string {
    const canOpen = canOpenPanels(caster);
    const hasSenses = sideInfo && hasVisibleSenses(caster);
    const speeds = (caster.computed?.creature?.speeds ?? {}) as Record<string, AcPart | number | boolean>;
    const ecArr = ecArrOf(caster);
    const preferred = speedTypeId(ecArr.preferred_speed_type) || speedTypeId(ftLayerValue(caster, 'default_preferred_speed_type'));

    const outerCls = sideInfo
        ? 'd-flex flex-column justify-content-start col-12 mb-2'
        : 'd-flex flex-column justify-content-start col-4 col-lg-5';
    let html = `<div class="${outerCls}">`;

    // SPEEDS
    html += `<div class="d-flex justify-content-start${sideInfo ? ' flex-wrap align-items-start' : ''} border-bottom" style="min-height:65px;">`;
    for (const speed of SPEED_TYPES) {
        const v = speedTotal(caster, speed.variable_name);
        if (v === 0) continue;

        let borderClass = 'border';
        let naturalTitle = '';
        // Default speed badge → cool blue-grey gradient; preferred → vivid green
        // highlighter (the preferred_speed_type stand-out).
        let badgeStyle = ' style="' + SPEED_BADGE_STYLE + '"';

        const isDerivable = speed.variable_name === 'speed_swimming'
            || speed.variable_name === 'speed_climbing'
            || speed.variable_name === 'speed_jumping';
        // Cluster normalization #3 (2026-06-10): natural = a speed OBJECT without the
        // `derived: true` flag (the standalone default_* flags are gone; a legacy
        // bare-number cached value was the derived shape).
        const speedIsNatural = (short: string): boolean => {
            const slot = speeds[short];
            return slot !== undefined && slot !== null && typeof slot === 'object' && slot.derived !== true;
        };
        let isNatural = false;
        if (isDerivable) {
            if (speed.variable_name === 'speed_swimming') isNatural = speedIsNatural('swimming');
            else if (speed.variable_name === 'speed_climbing') isNatural = speedIsNatural('climbing');
            else isNatural = speedHasExplicitLayer(caster, 'speed_jumping');
        }

        if (preferred === speed.item_id) {
            badgeStyle = ' style="' + PREFERRED_SPEED_BADGE_STYLE + '"';
        } else if (isNatural) {
            borderClass = 'border border-info border-2';
            naturalTitle = 'Natural ' + speed.variable_name.replace('speed_', '') + ' speed (manually set, not derived from walking)';
        }

        html += '<div class="d-flex flex-column justify-content-center text-center col-3.5 p-1"><div>';
        if (canOpen) {
            html += `<span role="button" data-ft-info-item-id="${escapeHtml(speed.item_id)}" ${csOpenPanelAttrs('movement-sense', casterId, { variable: speed.variable_name })}>${iconMap(speed.variable_name)}</span>`;
        } else {
            html += `<span class="user-select-none opacity-75" aria-hidden="true">${iconMap(speed.variable_name)}</span>`;
        }
        html += '</div><div>';
        const titleAttr = naturalTitle !== '' ? ` data-bs-toggle="tooltip" title="${escapeHtml(naturalTitle)}"` : '';
        html += `<span class="badge ${borderClass}"${badgeStyle}${titleAttr}>${v}</span>`;
        html += '</div></div>';
    }
    if (sideInfo && !hasSenses) {
        html += renderCombatExtrasCellsSideinfo(caster, casterId, true);
    }
    html += '</div>';

    // SENSES
    if (!sideInfo || hasSenses) {
        html += `<div class="d-flex justify-content-start${sideInfo ? ' flex-wrap align-items-start' : ''}" style="${sideInfo ? '' : 'min-height:65px;'}">`;
        for (const sense of SENSES) {
            const v = senseTotal(caster, sense.variable_name);
            if (v === 0) continue;
            html += '<div class="d-flex flex-column justify-content-center text-center col-3.5 p-1"><div>';
            if (canOpen) {
                html += `<span role="button" data-ft-info-item-id="${escapeHtml(sense.item_id)}" ${csOpenPanelAttrs('movement-sense', casterId, { variable: sense.variable_name })}>${iconMap(sense.variable_name)}</span>`;
            } else {
                html += `<span class="user-select-none opacity-75" aria-hidden="true">${iconMap(sense.variable_name)}</span>`;
            }
            html += '</div><div><span class="badge border" style="' + SENSE_BADGE_STYLE + '">' + v + '</span></div></div>';
        }
        if (num(ftLayerValue(caster, 'blindsight_blind')) !== 0) {
            html += '<div class="d-flex flex-column justify-content-center text-center col-3.5 p-1"><div>';
            if (canOpen) {
                html += `<span role="button" data-ft-info-item-id="${BLINDSIGHT_BLIND_UUID}" ${csOpenPanelAttrs('movement-sense', casterId, { variable: 'blindsight_blind' })}>${iconMap('blindsight_blind')}</span>`;
            } else {
                html += `<span class="user-select-none opacity-75" aria-hidden="true">${iconMap('blindsight_blind')}</span>`;
            }
            html += '</div><div></div></div>';
        }
        if (sideInfo && hasSenses) {
            html += renderCombatExtrasCellsSideinfo(caster, casterId, true);
        }
        html += '</div>';
    }

    html += '</div>';
    return html;
}

// ── ROW ORCHESTRATOR (render_creature_summary_row) ────────────────────────────
function renderCreatureSummaryRow(caster: CachedCaster, fallbackId: string, sideInfo: boolean): string {
    const casterId = casterIdOf(caster, fallbackId);

    // Lairs are item-like combat actors: no essences / vitality / speeds / senses to show. Strip the
    // whole summary row down to the action-point economy — a lair acts once per round, so its AP is the
    // only summary datum worth showing to run it in combat. Kept as the creature_summary pane so it
    // still live-updates via ftRerenderPcSheet when the lair spends / refreshes its action.
    if (itemTypeOf(caster) === 'lair') {
        const ap = renderActionBarAp(casterId);
        const lairRootCls = sideInfo ? 'ft-creature-summary ft-creature-summary--sideinfo-host mb-2' : 'mb-2';
        return `<div class="${lairRootCls} d-flex align-items-center justify-content-center gap-2 flex-wrap">`
            + `<span class="small text-muted d-inline-flex align-items-center gap-1">${iconMap('aps')} Lair action</span>`
            + (ap || '<span class="badge bg-secondary">1 / round</span>')
            + `</div>`;
    }

    const rootCls = sideInfo ? 'ft-creature-summary ft-creature-summary--sideinfo-host mb-3' : 'd-md-flex mb-3';
    const barsWrapCls = sideInfo
        ? 'col-12 mb-2 text-center'
        : 'col-lg-3 col-md-12 px-2 mx-lg-2 mb-1';

    let html = `<div class="${rootCls}">`;
    html += renderEssenceDefense(caster, casterId, sideInfo);
    html += `<div class="d-flex align-items-center justify-content-center mb-1 ${barsWrapCls}">${renderVManaEcacLuckBars(caster, casterId)}</div>`;
    html += renderSpeedSenses(caster, casterId, sideInfo);
    if (!sideInfo) {
        html += renderCombatExtras(caster, casterId);
    }
    html += '</div>';
    return html;
}

/**
 * Inline entry point for the creature card. Renders the summary row into #targetId
 * from the client cache (fetch-on-miss). Clears the target when the caster can't be
 * loaded so the card leaves no broken gap. fallbackId is the PHP-resolved caster id.
 */
export async function renderCreatureSummaryInline(
    casterId: string,
    targetId: string,
    sideInfo = false,
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }
    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    paintMarkup(target, caster ? renderCreatureSummaryRow(caster, casterId, !!sideInfo) : '');
}

/**
 * Compact HP + Mana readout for the action bar's full-screen dock (Diablo-ish, slim, no AC/luck/AP).
 * Reuses the same vitality/mana reads + side-panel click-throughs as the summary bars; sync (reads
 * casterCache directly — the bar already holds the blob). '' when the cache is cold. Two slim bars only.
 */
export function renderActionBarHpMana(casterId: string, casterSnapshot?: CachedCaster): string {
    // The action-bar renderer already holds the freshly applied canonical row. Prefer it over a
    // cache lookup so an encounter_creature_id/item_id alias cannot make HP/Mana lag one frame.
    const caster = casterSnapshot
        ?? (window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined);
    if (!caster) return '';
    const vCurrent = num(vit(caster, 'v_current'));
    const vMax = num(vit(caster, 'v_max'));
    const effRaw = vit(caster, 'effective_v_max');
    const effMax = (effRaw != null && effRaw !== '') ? num(effRaw) : vMax;
    const vPct = num(vit(caster, 'v_bar_v_percent'));
    const vTempPct = num(vit(caster, 'v_bar_v_temp_percent'));
    const vTemp = num(vit(caster, 'v_temp'));
    const cid = escapeHtml(casterId);
    let html = `<div class="ft-action-res-bar" role="button" ${csOpenPanelAttrs('health', casterId)}>`
        + `<span class="ft-action-res-ico">${iconMap('vitality', 16, 16)}</span>`
        + `<span class="ft-action-res-track"><span class="ft-action-res-fill ft-action-res-fill--hp" style="width:${vPct}%"></span>`
        + (vTempPct > 0 ? `<span class="ft-action-res-fill ft-action-res-fill--temp" style="width:${vTempPct}%"></span>` : '')
        + `<span class="ft-action-res-label">${vCurrent} / ${effMax}${vTemp ? ` (+${vTemp})` : ''}</span></span></div>`;

    const spirit = spiritMax(caster);
    const ecArr = ecArrOf(caster);
    const ecId = String(unwrap(ecArr.encounter_creature_id) ?? '');
    // This renderer is a projection inside an already-authorized host: the FT3D actor dock gates the
    // action bar, and the Party frame gates exact resources with canInspect. Do not apply a second
    // player/campaign permission test here; doing so can hide one resource from an otherwise valid bar
    // (and makes presentation depend on which authority lane mounted it). A real spirit pool is the
    // complete presentation condition. The general creature-summary renderer above keeps its own gate.
    const showMana = spirit > 0;
    if (showMana) {
        const isItemCreature = ecId === '';
        const manaDisplay = isItemCreature ? spirit : num(ecArr.mana);
        const manaPct = isItemCreature ? 100 : manaPercentOf(ecArr);
        html += `<div class="ft-action-res-bar" role="button" ${csOpenPanelAttrs('mana-spirit', casterId)}>`
            + `<span class="ft-action-res-ico">${iconMap('mana', 16, 16)}</span>`
            + `<span class="ft-action-res-track"><span class="ft-action-res-fill ft-action-res-fill--mana" style="width:${manaPct}%"></span>`
            + `<span class="ft-action-res-label">${manaDisplay} / ${spirit}</span></span></div>`;
    }
    return html;
}

/**
 * Action-bar dock: AP pips + the single reaction pip, as a fixed-width segment that leads the resources row
 * (HP/Mana/class bars flex to fill the rest). Combat-only (no encounter_id → ''). Reuses the SAME pip art +
 * remaining/max/reaction reads as the summary AP row, so the two always agree.
 */
export function renderActionBarAp(
    casterId: string,
    casterSnapshot?: CachedCaster,
    interactive = false,
    expanded = false,
): string {
    // The action-bar renderer already owns the snapshot it is painting. Prefer that exact row, as the
    // HP/Mana renderer does, so a route cache alias cannot make AP lag one websocket frame.
    const caster = casterSnapshot
        ?? (window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined);
    if (!caster) return '';
    const ecArr = ecArrOf(caster);
    if (!ecArr.encounter_id) return ''; // AP / reaction are a combat economy — nothing to show out of combat
    const darkMode = /(?:^|;\s*)dark_mode=1(?:;|$)/.test(document.cookie);
    const ap = actionPointCounts(ecArr);
    const pip = (src: string) => `<img class="ft-action-ap-pip" src="${CDN_ICONS}${src}" width="14" height="14" alt="" aria-hidden="true">`;
    let dots = '';
    for (let x = 0; x < ap.remaining; x++) dots += pip(darkMode ? 'ap_unused_dark_mode.png' : 'ap_unused.png');
    for (let x = 0; x < ap.spent; x++) dots += pip(darkMode ? 'ap_used_dark_mode.png' : 'ap_used.png');
    // Reaction pip: 1 = available; anything else = used. A spent reaction is stored as 0, which the
    // lean EC-blob build elides → arrives as undefined; in combat the slot always exists, so show the
    // crossed-out "used" pip for absent/0 rather than nothing (matches the summary AP row above).
    // EXCEPT lairs — they have no reaction economy (AP-only; see the lair branch in
    // renderCreatureSummaryRow), yet insert_encounter_creature seeds every EC with reaction=1, so
    // without this guard a lair would render a stray reaction pip. Suppress it for lairs entirely.
    const react = itemTypeOf(caster) === 'lair'
        ? ''
        : pip(num(ecArr.reaction) === 1
            ? (darkMode ? 'ap_reaction_dark_mode.png' : 'ap_reaction.png')
            : (darkMode ? 'ap_reaction_used_dark_mode.png' : 'ap_reaction_used.png'));
    if (!dots && !react) return '';
    // Badged pill (no divider before the reaction — owner 2026-06-26). In the FT3D actor dock the
    // pips are also the launcher for the compact Core Ability / AP controls fold-up. Keep non-dock
    // projections (notably the lair summary row) passive: they do not own an action-bar pane in which
    // the fold-up could render.
    if (interactive) {
        const cid = escapeHtml(casterId);
        return `<div class="ft-action-ap" data-ft-ap-interactive="1">`
            + `<button type="button" class="ft-action-ap__button" data-ft-ab-action="toggle-ap-fold" data-ft-caster-id="${cid}" aria-expanded="${expanded ? 'true' : 'false'}" data-bs-toggle="tooltip" data-bs-original-title="Action points · reaction · open controls">${dots}${react}</button>`
            + `</div>`;
    }
    return `<div class="ft-action-ap" data-bs-toggle="tooltip" data-bs-original-title="Action points · reaction">${dots}${react}</div>`;
}

/**
 * Standalone inline entry point for the V·Mana·AC·Luck·AP bars
 * (build_v_mana_ecac_luck_bars). Used by the non-summary-row callers — the player
 * character sheet, the encounter creature table-list, and the encounter report —
 * which embed the bars on their own (the creature card renders them as part of the
 * summary row above). `narrow` mirrors the PHP $in_narrow_summary flag (extra padding
 * for the side-info host). Fetch-on-miss; clears the target when unavailable.
 */
export async function renderVManaEcacLuckBarsInline(
    casterId: string,
    targetId: string,
    narrow = false,
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }
    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    paintMarkup(target, caster ? renderVManaEcacLuckBars(caster, casterIdOf(caster, casterId), !!narrow) : '');
}
